import type { InvisibleRecaptchaHandle } from '@/scopes/common/components/InvisibleRecaptcha/InvisibleRecaptcha'
import * as React from 'react'
import { createAuthActions } from '@/scopes/auth/domain/actions'
import { AuthForm } from '@/scopes/auth/domain/types'
import { createAuthApiClient } from '@/scopes/auth/infrastructure/authApiClient'
import { AuthDependenciesProvider, useAuthRecaptcha } from '@/scopes/auth/infrastructure/dependencies'
import { focusFormTitle } from '@/scopes/auth/infrastructure/formTitle'
import { createAuthStore, createInitialAuthState } from '@/scopes/auth/infrastructure/store'
import { useAuthStore } from '@/scopes/auth/infrastructure/useAuthStore'
import { AuthHeader } from '@/scopes/auth/pages/components/AuthHeader'
import { AuthLoading } from '@/scopes/auth/pages/components/AuthLoading'
import { CreateAccountForm } from '@/scopes/auth/pages/components/create-account/CreateAccountForm'
import { LoginForm } from '@/scopes/auth/pages/components/login/LoginForm'
import { ResetPasswordForm } from '@/scopes/auth/pages/components/reset-password/ResetPasswordForm'
import { getIsPageInitializing } from '@/scopes/auth/pages/selectors/authSelectors'
import { Footer } from '@/scopes/common/components'
import { InvisibleRecaptcha } from '@/scopes/common/components/InvisibleRecaptcha/InvisibleRecaptcha'
import { MetaCanonical } from '@/scopes/common/components/MetaCanonical'
import { MetaDescription } from '@/scopes/common/components/MetaDescription'
import { MetaRobots } from '@/scopes/common/components/MetaRobots'
import { MetaTitle } from '@/scopes/common/components/MetaTitle'
import { getContextItemId } from '@/scopes/common/domain/policies/contextItemId'
import { useConfiguration, useConfigurationClient } from '@/scopes/common/infrastructure/config/ConfigProvider'
import { browserNavigationEvents } from '@/scopes/common/infrastructure/dom/browserNavigationEvents'
import { useLocation } from '@/scopes/common/infrastructure/dom/location'
import { scrollToTop } from '@/scopes/common/infrastructure/dom/scrollToTop'
import { createNewRelicLogger } from '@/scopes/common/infrastructure/logging/newRelicLogger'
import queryStringClient from '@/scopes/common/infrastructure/query-strings/queryStringClient'
import { userAuthenticationClient } from '@/scopes/common/infrastructure/user'
import styles from './Auth.module.css'

const authForms: Record<AuthForm, React.ComponentType> = {
  login: LoginForm,
  createAccount: CreateAccountForm,
  resetPassword: ResetPasswordForm,
}

function AuthContent() {
  const activeForm = useAuthStore(state => state.activeForm)
  const isPageInitializing = useAuthStore(getIsPageInitializing)
  const recaptchaRef = useAuthRecaptcha()
  const ActiveForm = authForms[activeForm]

  return (
    <div className={styles.layout}>
      <AuthHeader />
      <main className={styles.page}>
        {isPageInitializing
          ? <AuthLoading />
          : (
              <div className={styles.card}>
                <InvisibleRecaptcha ref={recaptchaRef} />
                <ActiveForm />
              </div>
            )}
      </main>
      <Footer className={isPageInitializing ? styles.footerInitializing : undefined} />
    </div>
  )
}

export function Auth() {
  const location = useLocation()
  const [store] = React.useState(() => createAuthStore(createInitialAuthState(location.search())))
  const recaptchaRef = React.useRef<InvisibleRecaptchaHandle>(null)
  const configurationClient = useConfigurationClient()
  const siteName = useConfiguration('siteName')
  const canonicalUrl = `${configurationClient.getField('domainUrl')}${configurationClient.getField('links.login')}`
  const actions = React.useMemo(() => createAuthActions({
    apiClient: createAuthApiClient({ contextItemId: getContextItemId(siteName) }),
    store,
    configurationClient,
    location,
    queryStringClient,
    userAuthenticationClient,
    logger: createNewRelicLogger({ scope: 'auth' }),
    scrollToTop,
    focusFormTitle,
    browserNavigationEvents,
  }), [store, configurationClient, location, siteName])

  React.useEffect(() => {
    void actions.pageLoaded()
  }, [actions])

  return (
    <>
      <MetaTitle titleTranslationTag="auth_meta_title" />
      <MetaDescription descriptionTranslationTag="auth_meta_description" />
      <MetaCanonical url={canonicalUrl} />
      <MetaRobots index follow />
      <AuthDependenciesProvider dependencies={{ actions, store, recaptcha: recaptchaRef }}>
        <AuthContent />
      </AuthDependenciesProvider>
    </>
  )
}
